import { ErrorCode, RequestError } from '../types/actions';
import { createLocalHostApiFactory } from '../utils/local-host';
import {
  Customer,
  Discount,
  Order,
  OrderApi,
  OrderLineItem,
  OrderLineItemAppliedDiscount,
  OrderLineItemDiscount,
} from './types';
import { uuid } from '../../utils/uuid';

/**
 * Adds customer to the order.
 *
 * @param customer Customer to add to order.
 */
const addCustomer: (customer: Customer) => Promise<void> =
  createLocalHostApiFactory((customer: Customer) => {
    window.sessionStorage.setItem('customer', JSON.stringify(customer));
  });

/**
 * Retrieves customer.
 *
 * @returns Customer tied to the order.
 * @throws {RequestError}
 */
const getCustomer: () => Promise<Customer> = createLocalHostApiFactory(() => {
  const customerString = sessionStorage.getItem('customer');
  if (!customerString) {
    throw new RequestError(
      ErrorCode.BAD_REQUEST,
      'There is no customer in the order'
    );
  }

  const customer = JSON.parse(customerString) as Customer;

  return customer;
});

/**
 * Removes customer from the order.
 *
 * @throws {RequestError}
 */
const removeCustomer: () => Promise<void> = createLocalHostApiFactory(() => {
  const customerString = sessionStorage.getItem('customer');
  if (!customerString) {
    throw new RequestError(
      ErrorCode.BAD_REQUEST,
      'There is no customer in the order'
    );
  }

  sessionStorage.removeItem('customer');
});

function createOrderLineItemDiscount(
  discount: Discount
): OrderLineItemDiscount {
  return {
    uid: uuid(),
    name: discount.name,
    type: discount.type,
    scope: discount.scope,
    amountMoney: {
      amount: BigInt(discount?.amountMoney?.amount),
      currency: discount?.amountMoney?.currency,
    },
    appliedMoney: {
      amount: BigInt(discount?.amountMoney?.amount),
      currency: discount?.amountMoney?.currency,
    },
    metadata: discount.metadata,
  };
}

/**
 * Adds a discount to the order.
 *
 * @param discount Discount to add.
 * @returns UID of the added discount.
 */
const addDiscount: (discount: Discount) => Promise<string> =
  createLocalHostApiFactory((discount: Discount) => {
    const orderLineItemDiscount = createOrderLineItemDiscount(discount);
    const order = JSON.parse(sessionStorage.getItem('order') ?? '{}');

    // Add discount to the order line item.
    if (discount.lineItemId) {
      const orderLineItem = (order.lineItems ?? []).find(
        (orderLineItem: OrderLineItem) => {
          return orderLineItem.uid === discount.lineItemId;
        }
      );

      if (!orderLineItem) {
        throw new RequestError(
          ErrorCode.BAD_REQUEST,
          `There is no order line item with ID ${discount.lineItemId}`
        );
      }

      const appliedDiscounts = orderLineItem.appliedDiscounts ?? [];
      appliedDiscounts.push({
        uid: uuid(),
        discountUid: orderLineItemDiscount.uid,
        appliedMoney: orderLineItemDiscount.appliedMoney,
      });

      orderLineItem.appliedDiscounts = appliedDiscounts;
    }

    const discounts = order.discounts ?? [];
    discounts.push(orderLineItemDiscount);
    order.discounts = discounts;

    setOrder(order);

    return orderLineItemDiscount.uid;
  });

/**
 * Removes discount from the order.
 *
 * @param discountId Id of the discount to remove.
 * @throws {RequestError}
 */
const removeDiscount: (discountId: string) => Promise<void> =
  createLocalHostApiFactory((discountId: string) => {
    const order = JSON.parse(sessionStorage.getItem('order') ?? '{}');
    const discounts = order.discounts ?? [];

    // Remove the discount with the discount ID from order.discounts.
    const newDiscounts = discounts.filter(
      (discount: OrderLineItemDiscount) => discount.uid !== discountId
    );

    if (discounts.length === newDiscounts.length) {
      throw new RequestError(
        ErrorCode.BAD_REQUEST,
        `There is no discount with discount ID ${discountId}`
      );
    }

    // Remove the discount from the order line item.
    for (const orderLineItem of order.lineItems ?? []) {
      if (orderLineItem.appliedDiscounts) {
        orderLineItem.appliedDiscounts = orderLineItem.appliedDiscounts.filter(
          (orderLineItemAppliedDiscount: OrderLineItemAppliedDiscount) =>
            orderLineItemAppliedDiscount.discountUid !== discountId
        );
      }
    }

    order.discounts = newDiscounts;
    setOrder(order);
  });

/**
 * Retrieves the current order.
 *
 * @returns Current order object.
 */
const retrieve: () => Promise<Order> = createLocalHostApiFactory(() => {
  const order = JSON.parse(sessionStorage.getItem('order') ?? '{}');

  return order;
});

function setOrder(order: Order) {
  sessionStorage.setItem('order', JSON.stringify(order));
}

function setCustomer(customer: Customer) {
  window.sessionStorage.setItem('customer', JSON.stringify(customer));
}

const order: OrderApi = {
  addCustomer,
  getCustomer,
  addDiscount,
  removeCustomer,
  removeDiscount,
  retrieve,
};

export { order, setCustomer, setOrder };
